import type {
  TImageFiltersRequest,
  TImageToolExportOperation,
  TImageToolExportPlacement,
} from "../../../../services/api-image-tools";
import { faceRegionsToExport, TFaceBlurRegion } from "./faceCensorshipGeometry";
import type { TImageFilterId } from "./ImageToolsSections";

export const DEFAULT_TATTOO_BLUR_RADIUS = 20;

export const FACE_BLUR_EXPORT_PARAMS = {
  mask_expansion_ratio: 0.12,
  feather_ratio: 0.01,
  blur_radius_ratio: 0.08,
} as const;

export const FACE_CENSORSHIP_DETECTION_CONFIG = {
  mode: "detect" as const,
  apply_params: {
    effect: "blur" as const,
    ...FACE_BLUR_EXPORT_PARAMS,
  },
};

export const buildImageToolsProcessingRequest = ({
  selectedFilters,
  coverAssetUrl,
}: {
  selectedFilters: readonly TImageFilterId[];
  coverAssetUrl?: string;
}): TImageFiltersRequest => {
  const hasFaceCensorship = selectedFilters.includes("hide-faces");
  const request: TImageFiltersRequest = {};

  if (selectedFilters.includes("cover-privates")) {
    request.censorship = hasFaceCensorship
      ? { mode: "detect" }
      : { mode: "apply", asset_url: coverAssetUrl };
  }
  if (hasFaceCensorship) {
    request.face_censorship = FACE_CENSORSHIP_DETECTION_CONFIG;
    return request;
  }
  if (selectedFilters.includes("blur-tattoos")) {
    request.tattoo = {
      mode: "apply",
      apply_params: { blur_radius: DEFAULT_TATTOO_BLUR_RADIUS },
    };
  }
  if (selectedFilters.includes("remove-background")) {
    request.background = { mode: "apply" };
  }
  return request;
};

export type TOrderedImageTool =
  | {
      id: "blur-tattoos";
      mode: "tattoo_blur";
      blurRadius?: number;
    }
  | {
      id: "cover-privates" | "hide-faces";
      mode: "overlay";
      assetUrl: string;
      placements: TImageToolExportPlacement[];
    }
  | {
      id: "hide-faces";
      mode: "face_blur";
      regions: TFaceBlurRegion[];
    }
  | {
      id: "remove-background";
      mode: "background_remove";
      alphaThreshold?: number | null;
    };

const IMAGE_TOOL_EXPORT_ORDER: Record<TOrderedImageTool["id"], number> = {
  "blur-tattoos": 0,
  "cover-privates": 1,
  "hide-faces": 2,
  "remove-background": 3,
};

export const buildOrderedImageToolExportOperations = (
  tools: readonly TOrderedImageTool[],
): TImageToolExportOperation[] =>
  [...tools]
    .sort(
      (left, right) =>
        IMAGE_TOOL_EXPORT_ORDER[left.id] - IMAGE_TOOL_EXPORT_ORDER[right.id],
    )
    .flatMap<TImageToolExportOperation>((tool) => {
      if (tool.mode === "tattoo_blur") {
        return [
          {
            operation_type: "tattoo_blur",
            blur_radius: tool.blurRadius ?? DEFAULT_TATTOO_BLUR_RADIUS,
          },
        ];
      }
      if (tool.mode === "background_remove") {
        return [
          {
            operation_type: "background_remove",
            alpha_threshold: tool.alphaThreshold ?? null,
          },
        ];
      }
      if (tool.mode === "face_blur") {
        const regions = faceRegionsToExport(tool.regions);
        return regions.length
          ? [
              {
                operation_type: "face_blur" as const,
                regions,
                ...FACE_BLUR_EXPORT_PARAMS,
              },
            ]
          : [];
      }

      return [
        {
          operation_type: "overlay" as const,
          asset_url: tool.assetUrl,
          placements: tool.placements.map(({ x, y, width, height }) => ({
            x,
            y,
            width,
            height,
          })),
        },
      ];
    });
